// Takes the location out of a photo or video in the browser before it uploads (#352), with the cleaner the Worker runs
// (shared/src/mediaClean.ts). The location never crosses the network. It copies the file without its hidden metadata,
// not re-encoded, reading the file with `File.slice` so a 100 MB video costs the page a few megabytes. Then it reads
// the copy back; only a copy with no location is returned.
//
// Where the file can't be cleaned (a type with no cleaner, a file that doesn't parse), a picture is drawn again and
// saved as a JPEG with no metadata, which loses some quality and never sends the location. A video is not sent.
import { CleanError, planIsClean, refusalMessage, planMedia, rawHasLocation, sortEdits, type Edit, type MediaPlan } from "@bloom/shared";
import { track } from "./analytics.ts";

export { CleanError };

/** Why a file was not sent, in the words shown to the person. */
export class LocationRefused extends Error {}

export const VIDEO_REFUSED = refusalMessage("video", "location");
export const PICTURE_REFUSED = refusalMessage("picture", "location");

/** How a file got past the cleaner, for the counts of gaps that show where a type needs its own cleaner. */
export type Outcome = "clean" | "unchanged" | "reencoded" | "refused";

export interface Safe {
  file: File;
  outcome: Outcome;
  /** The file held a location before (it was removed). */
  hadLocation: boolean;
}

const openFile = (f: Blob) => async (offset: number) => f.slice(offset).stream() as ReadableStream<Uint8Array>;

/** A zero-filled block shared by every blanked range. */
const ZEROS = new Uint8Array(1024 * 1024);

/** The file with the plan's edits applied, as a Blob over slices of the original: nothing is copied but the edits. */
export function applyEditsToBlob(file: Blob, edits: Edit[]): Blob {
  const parts: BlobPart[] = [];
  let at = 0;
  for (const e of sortEdits(edits)) {
    parts.push(file.slice(at, e.at));
    if (e.insert?.length) parts.push(e.insert as Uint8Array<ArrayBuffer>);
    for (let z = e.zeros ?? 0; z > 0; z -= ZEROS.length) parts.push(z >= ZEROS.length ? ZEROS : ZEROS.subarray(0, z));
    at = e.at + e.remove;
  }
  parts.push(file.slice(at));
  return new Blob(parts);
}

/** Files up to this size are also searched byte by byte for a written-out place (the rest only by their structure). */
export const RAW_SCAN_MAX = 32 * 1024 * 1024;
const rawShowsLocation = async (f: Blob) => f.size <= RAW_SCAN_MAX && rawHasLocation(new Uint8Array(await f.arrayBuffer()));

const plan = (file: Blob, type: string): Promise<MediaPlan | null> => planMedia(openFile(file), file.size, type || "application/octet-stream");

/**
 * The file without its location, checked. A file that isn't a picture or video comes back as it is. Throws
 * CleanError when the file should be cleaned and can't be, and when the copy still shows a location.
 */
export async function stripLocation(file: File): Promise<Safe> {
  const p = await plan(file, file.type);
  if (!p) return { file, outcome: "unchanged", hadLocation: false };
  const retyped = p.mime !== file.type;
  if (!p.edits.length && !retyped) {
    if (await rawShowsLocation(file)) throw new CleanError("the file shows a location the cleaner has no way to remove", p.subject);
    return { file, outcome: "clean", hadLocation: p.location };
  }
  const blob = p.edits.length ? applyEditsToBlob(file, p.edits) : file;
  const out = new File([blob], file.name, { type: p.mime, lastModified: file.lastModified });
  // Read the copy back: only a copy with no location goes up.
  const check = await plan(out, out.type);
  if (!check || !planIsClean(check) || (await rawShowsLocation(out))) throw new CleanError("the cleaned copy still shows a location", p.subject);
  return { file: out, outcome: "clean", hadLocation: p.location };
}

/** A picture drawn again and saved as a JPEG: no metadata survives, and the orientation is applied. */
export async function reencodeAsJpeg(file: File): Promise<File> {
  const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
  try {
    const canvas = document.createElement("canvas");
    canvas.width = bmp.width;
    canvas.height = bmp.height;
    const g = canvas.getContext("2d");
    if (!g) throw new Error("no canvas");
    // JPEG has no transparency: the picture goes on white.
    g.fillStyle = "#fff"; // not a theme color: it is the picture's own background
    g.fillRect(0, 0, canvas.width, canvas.height);
    g.drawImage(bmp, 0, 0);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
    canvas.width = canvas.height = 0;
    if (!blob || blob.type !== "image/jpeg") throw new Error("no JPEG");
    return new File([blob], `${(file.name || "photo").replace(/\.[^.]*$/, "") || "photo"}.jpg`, { type: "image/jpeg", lastModified: file.lastModified });
  } finally {
    bmp.close();
  }
}

/** Tells analytics (no names, no places) that a file got past the cleaner only by the fallback, or not at all. */
function countGap(outcome: Outcome, kind: string) {
  console.warn(`[bloom] upload location gap: ${outcome} (${kind})`);
  track("upload_location_gap", { outcome, kind });
}

/**
 * What to upload for `file`: the same picture or video without its location, or throws LocationRefused with the words
 * to show. Never returns a file that still shows a location.
 */
export async function makeSafe(file: File): Promise<Safe> {
  try {
    return await stripLocation(file);
  } catch (e) {
    const subject = e instanceof CleanError ? e.subject : file.type.startsWith("video/") ? "video" : "picture";
    // The words name the real reason: a location, a format Bloom has no cleaner for, or a file it could not check.
    const refused = e instanceof CleanError ? refusalMessage(subject, e.reason, e.label) : refusalMessage(subject, "location");
    // A GIF is not drawn again: that would send one still picture in place of the animation.
    if (subject === "video" || file.type === "image/gif") {
      countGap("refused", file.type || "video");
      throw new LocationRefused(refused);
    }
    try {
      const jpeg = await reencodeAsJpeg(file);
      const checked = await stripLocation(jpeg);
      countGap("reencoded", file.type || "picture");
      return { file: checked.file, outcome: "reencoded", hadLocation: true };
    } catch {
      countGap("refused", file.type || "picture");
      throw new LocationRefused(refused);
    }
  }
}
